import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import ErrorMessage from "../../components/ErrorMessage";
import toast from "react-hot-toast";
import { QrCode } from "../../types/globalTypes";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import GridTableQrcode from "@/components/QrCodeSettings/GridTableQrcode";
import QrCodeCard from "@/components/QrCodeSettings/QrCodeCard";
import AddQrCode from "@/components/QrCodeSettings/AddQrCode";
import { Button } from "@/components/ui/button";
import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

function QrCodeSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  useEffect(() => {
    if (!token) return;

    async function getQrCodes() {
      const url = `${BASE_URL}/api/dashboard/qr-codes?page=${page}&locale=${lang}`;

      setIsLoading(true);
      setError(null);
      setNeedRefetch(false);

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: token,
          },
        });

        if (!response.data.success) {
          setError(response.data.message);
          setIsLoading(false);
          return;
        }

        setQrCodes(response.data?.qrcodes);
        setTotalPages(response.data.pagination.totalPages || 1);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t("qrSettings.fetchFailed") as string);
          setError(message);
          toast.error(message);
        } else {
          setError(
            t("qrSettings.unexpectedError") as string,
          );
          toast.error(
            t("qrSettings.unexpectedError") as string,
          );
        }
      } finally {
        setIsLoading(false);
      }
    }

    getQrCodes();
  }, [BASE_URL, lang, needRefetch, page, token, t]);

  if (isLoading)
    return (
      <>
        <div className="flex flex-col gap-6">
          <Skeleton className="ms-auto h-[50px] w-[100px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="self-start text-2xl font-semibold">
          {t("qrSettings.title")}
        </h1>
        {/* Will Replaced */}
        <AddQrCode  setNeedRefetch={setNeedRefetch} />
      </div>

      <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6">
        {qrCodes?.length === 0 ? (
          <>
            <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
              <AlertTitle className="text-center text-xl">
                {t("qrSettings.notFound")}
              </AlertTitle>
              <AlertDescription className="my-5 text-center font-semibold">
                {t("qrSettings.empty")}
              </AlertDescription>
            </Alert>
          </>
        ) : (
          <GridTableQrcode>
            {qrCodes?.map((qrCode, index) => (
              <QrCodeCard
                setNeedRefetch={setNeedRefetch}
                key={index}
                _id={qrCode._id}
                link={qrCode.link}
                serial={qrCode.serial}
                data={qrCode.data}
                type={qrCode.type}
                floor={qrCode.floor}
                table={qrCode.table}
                branch={qrCode.branch}
                isAvailableNow={qrCode.isAvailableNow}
              />
            ))}
          </GridTableQrcode>
        )}
      </div>

      {/* Pagination Controls */}
      {qrCodes?.length > 0 && (
        <div className="mt-5 flex justify-center gap-3">
          <Button
            className="bg-gray-400 dark:bg-gray-400"
            disabled={page <= 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            {lang === "ar" ? <FaAnglesRight /> : <FaAnglesLeft />}
          </Button>

          <div className="flex items-center gap-2">
            {/* Always show the first page */}
            <Button
              key={1}
              variant={1 === page ? "outline" : "default"}
              onClick={() => setPage(1)}
              className={`text-black hover:bg-white ${1 === page
                ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
                : "bg-white text-black"
                }`}
            >
              1
            </Button>

            {/* Show dots if the current page is far from the start */}
            {page > 3 && (
              <span className="hidden px-2 sm:inline-block">...</span>
            )}

            {/* Show buttons around the current page */}
            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .filter(
                (pageNum) =>
                  pageNum > 1 &&
                  pageNum < totalPages &&
                  Math.abs(pageNum - page) <= 1,
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "outline" : "default"}
                  onClick={() => setPage(pageNum)}
                  className={`text-black hover:bg-white ${pageNum === page
                    ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
                    : "bg-white text-black"
                    }`}
                >
                  {pageNum}
                </Button>
              ))}

            {/* Show dots if the current page is far from the end */}
            {page < totalPages - 2 && (
              <span className="hidden px-2 sm:inline-block">...</span>
            )}

            {/* Always show the last page */}
            {totalPages > 1 && (
              <Button
                key={totalPages}
                variant={totalPages === page ? "outline" : "default"}
                onClick={() => setPage(totalPages)}
                className={`text-black hover:bg-white ${totalPages === page
                  ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
                  : "bg-white text-black"
                  }`}
              >
                {totalPages}
              </Button>
            )}
          </div>

          <Button
            className="bg-gray-400 dark:bg-gray-400"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {lang === "ar" ? <FaAnglesLeft /> : <FaAnglesRight />}
          </Button>
        </div>
      )}
    </div>
  );
}

export default QrCodeSettings;
